import React, { useState, useEffect } from 'react';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import Dialog from './Dialog';
import StockCard from './StockCard';
import './OptionBuilder.css';
import { calculateImpliedVolatility, daysBetween, isValidDate, calculateBlackScholesDelta, calculateBlackScholesGamma, calculateBlackScholesTheta, calculateBlackScholesVega } from './financial';

const OptionPositionRow = ({
  item,
  builderId,
  isEditing,
  onUpdate,
  onStartEdit,
  onStopEdit,
  onCancelEdit,
  onDelete,
  ivInputValue,
  setIvInputValue,
  stockPrice,
  interest,
  dividendRatio,
  tradingDate,
}) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `position-${builderId}-${item.id}`,
    data: {
      type: 'position',
      builderId,
      optionId: item.id,
      cardType: item.cardType,
    },
    disabled: isEditing,
  });

  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
    : undefined;

  let iv = 'NA';
  const manualIv = Number(item.impliedVolatility);

  if (Number.isFinite(manualIv) && manualIv > 0) {
    iv = manualIv;
  } else if (stockPrice > 0) {
    const days = daysBetween(tradingDate, item.expiry); // whole days between dates
    const T = days / 365; // years as whole-days / 365
    iv = calculateImpliedVolatility(item.premium, stockPrice, item.strike, T, interest, dividendRatio, item.type.toLowerCase());
  }

  // Compute Greeks (readonly) when we have a numeric implied volatility
  let delta = 'NA';
  let gamma = 'NA';
  let theta = 'NA';
  let vega = 'NA';
  if (typeof iv === 'number' && stockPrice > 0) {
    const days = daysBetween(tradingDate, item.expiry);
    const T = days / 365;
    const optType = item.type.toLowerCase();
    delta = calculateBlackScholesDelta(stockPrice, item.strike, T, interest, iv, dividendRatio, optType);
    gamma = calculateBlackScholesGamma(stockPrice, item.strike, T, interest, iv, dividendRatio);
    theta = calculateBlackScholesTheta(stockPrice, item.strike, T, interest, iv, dividendRatio, optType);
    // calculate raw vega (per 1.0 = 100% vol), then convert to dollars per 1% (common UI convention)
    const vegaRaw = calculateBlackScholesVega(stockPrice, item.strike, T, interest, iv, dividendRatio);
    vega = vegaRaw * 0.01; // now vega is dollars per 1% change, per single option (per share)
  }

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={isDragging ? 'position-row dragging' : 'position-row'}
    >
      <td>
        <button
          type="button"
          className="drag-handle"
          aria-label="Drag option position"
          title={isEditing ? 'Save or cancel edits before dragging' : 'Drag to another combo'}
          disabled={isEditing}
          {...listeners}
          {...attributes}
        >
          ::
        </button>
        {isEditing ? (
          <select
            value={item.type}
            onChange={(e) => onUpdate(item.id, 'type', e.target.value)}
          >
            <option value="Call">Call</option>
            <option value="Put">Put</option>
          </select>
        ) : (
          <span className={`option-type ${item.type.toLowerCase()}`}>{item.type}</span>
        )}
      </td>
      <td>
        {isEditing ? (
          <input
            type="date"
            value={item.expiry}
            onChange={(e) => onUpdate(item.id, 'expiry', e.target.value)}
            className="p-1 border rounded"
          />
        ) : (
          item.expiry
        )}
      </td>
      <td>
        {isEditing ? (
          <input
            type="number"
            value={item.strike}
            onChange={(e) => onUpdate(item.id, 'strike', e.target.value)}
          />
        ) : (
          `$${Number(item.strike).toFixed(2)}`
        )}
      </td>
      <td>
        {isEditing ? (
          <input
            type="number"
            value={item.premium}
            onChange={(e) => onUpdate(item.id, 'premium', e.target.value)}
          />
        ) : (
          `$${Number(item.premium).toFixed(2)}`
        )}
      </td>
      <td>
        {isEditing ? (
          <input
            type="number"
            value={item.contract}
            onChange={(e) => onUpdate(item.id, 'contract', e.target.value)}
            step="1"
          />
        ) : (
          item.contract
        )}
      </td>
      <td>
        {isEditing ? (
          <input
            type="number"
            value={ivInputValue}
            onChange={(e) => {
              const value = e.target.value;
              setIvInputValue(value);
              if (value === '' || value === '-' || value === '.' || value === '-.' || value.endsWith('.')) {
                onUpdate(item.id, 'impliedVolatility', '');
                return;
              }

              const numericValue = Number(value);
              onUpdate(
                item.id,
                'impliedVolatility',
                Number.isFinite(numericValue) ? String(numericValue / 100) : ''
              );
            }}
            min="0.0001"
            step="0.01"
          />
        ) : (
          typeof iv === 'number' ? (iv * 100).toFixed(2) + '%' : iv
        )}
      </td>
      <td className="greeks-column">
        {typeof delta === 'number' ? (
          <div>
            <div><strong>Delta:</strong> {delta.toFixed(4)}</div>
            <div><strong>Gamma:</strong> {gamma.toFixed(4)}</div>
            <div><strong>Theta:</strong> {theta.toFixed(4)}</div>
            <div><strong>V:</strong> {vega.toFixed(4)}</div>
          </div>
        ) : (
          'NA'
        )}
      </td>
      <td>
        ${(item.premium * item.contract * 100).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}
      </td>
      <td>
        {isEditing ? (
          <>
            <button onClick={() => onStopEdit()} className="save-btn">
              Save
            </button>
            <button onClick={onCancelEdit} className="cancel-btn">
              Cancel
            </button>
          </>
        ) : (
          <button onClick={() => onStartEdit(item.id)} className="edit-btn">
            Edit
          </button>
        )}
        <button onClick={onDelete} className="delete-btn">
          Delete
        </button>
      </td>
    </tr>
  );
};

const OptionBuilder = ({ builderId, options, onRemoveOption, onUpdateOption, onRemoveBuilder, onCopyBuilder, onAddStock, onAddOption, hasStock, onReplaceOption, title, onUpdateTitle, interest, dividendRatio, tradingDate, equityPrice, setToast }) => {
  const { isOver, setNodeRef } = useDroppable({
    id: `combo-${builderId}`,
    data: {
      type: 'combo',
      builderId,
    },
  });
  const [isDeleteComboDialogOpen, setIsDeleteComboDialogOpen] = useState(false);
  const [isDeleteItemDialogOpen, setIsDeleteItemDialogOpen] = useState(false);
  const [deletingItemId, setDeletingItemId] = useState(null);
  const [editingItemId, setEditingItemId] = useState(null);
  const [originalItem, setOriginalItem] = useState(null);
  const [ivInputValue, setIvInputValue] = useState('');
  const [isCollapsed, setIsCollapsed] = useState(false);

  const handleUpdate = (id, field, value) => {
    onUpdateOption(id, field, value);
  };

  const isEditing = React.useCallback((id) => id === editingItemId, [editingItemId]);

  const startEditing = React.useCallback((id) => {
    const itemToEdit = options.find((o) => o.id === id);
    setOriginalItem({ ...itemToEdit });
    if (itemToEdit?.cardType === 'option') {
      const nextIvInputValue =
        itemToEdit.impliedVolatility === '' || itemToEdit.impliedVolatility === null || itemToEdit.impliedVolatility === undefined
          ? ''
          : (Number(itemToEdit.impliedVolatility) * 100).toFixed(2);
      setIvInputValue(nextIvInputValue);
    } else {
      setIvInputValue('');
    }
    setEditingItemId(id);
  }, [options, setOriginalItem, setEditingItemId]);

  const getCalculatedImpliedVolatility = React.useCallback((item) => {
    const stockPrice = Number(equityPrice);
    const strike = Number(item?.strike);
    const premium = Number(item?.premium);

    if (
      !Number.isFinite(stockPrice) ||
      stockPrice <= 0 ||
      !Number.isFinite(strike) ||
      strike <= 0 ||
      !Number.isFinite(premium) ||
      premium <= 0 ||
      !item?.expiry ||
      !isValidDate(item.expiry) ||
      !item?.type
    ) {
      return null;
    }

    const days = daysBetween(tradingDate, item.expiry);
    const T = days / 365;
    if (T <= 0) {
      return null;
    }

    const iv = calculateImpliedVolatility(
      premium,
      stockPrice,
      strike,
      T,
      interest,
      dividendRatio,
      item.type.toLowerCase()
    );

    return Number.isFinite(Number(iv)) && Number(iv) > 0 ? Number(iv) : null;
  }, [dividendRatio, equityPrice, interest, tradingDate]);

  const stopEditing = () => {
    const item = options.find(o => o.id === editingItemId);
    if (item) {
      if (item.cardType === 'option') {
        if (!item.strike || item.strike <= 0) {
          setToast({ message: 'Strike price must be greater than 0.', type: 'error' });
          return;
        }
        if (!item.expiry || !isValidDate(item.expiry)) {
          setToast({ message: 'Valid Expiry date is required.', type: 'error' });
          return;
        }
        if (!item.premium || item.premium <= 0) {
          setToast({ message: 'Premium must be greater than 0.', type: 'error' });
          return;
        }
        let impliedVolatility = item.impliedVolatility;
        if (impliedVolatility === '' || impliedVolatility === null || impliedVolatility === undefined) {
          const calculatedIv = getCalculatedImpliedVolatility(item);
          if (calculatedIv !== null) {
            impliedVolatility = calculatedIv;
            onUpdateOption(item.id, 'impliedVolatility', calculatedIv);
          }
        }
        if (!impliedVolatility || Number(impliedVolatility) <= 0) {
          setToast({ message: 'Implied volatility must be greater than 0.', type: 'error' });
          return;
        }
        if (item.contract === '' || isNaN(item.contract) || item.contract % 1 !== 0) {
          setToast({ message: 'Contracts must be a whole number.', type: 'error' });
          return;
        }
      } else if (item.cardType === 'stock') {
        if (!item.price || item.price <= 0) {
          setToast({ message: 'Price must be greater than 0.', type: 'error' });
          return;
        }
        if (item.shares === '' || isNaN(item.shares) || item.shares % 1 !== 0) {
          setToast({ message: 'Shares must be a whole number.', type: 'error' });
          return;
        }
      }
    }
    setEditingItemId(null);
    setOriginalItem(null);
    setIvInputValue('');
  };

  const cancelEditing = () => {
    if (originalItem) {
      onReplaceOption(originalItem.id, originalItem);
    }
    setIvInputValue('');
    stopEditing();
  };

  useEffect(() => {
    const newItem = options.find(o => o.isNew);
    if (newItem && !isEditing(newItem.id)) {
      startEditing(newItem.id);
      onUpdateOption(newItem.id, 'isNew', false);
    }
  }, [options, isEditing, startEditing, onUpdateOption]);

  const itemToDelete = options.find(o => o.id === deletingItemId);
  const deleteMessage = itemToDelete
    ? `Are you sure you want to delete this ${itemToDelete.cardType}?`
    : 'Are you sure you want to delete this item?';

  const comboTotals = options.reduce((totals, item) => {
    if (item.cardType === 'stock') {
      const price = Number(item.price);
      const shares = Number(item.shares);
      if (Number.isFinite(price) && Number.isFinite(shares)) {
        totals.value += price * shares;
      }
      if (Number.isFinite(shares)) {
        totals.delta += shares;
      }
      return totals;
    }

    if (item.cardType === 'option') {
      const premium = Number(item.premium);
      const contracts = Number(item.contract);
      const positionSize = Number.isFinite(contracts) ? contracts * 100 : 0;

      if (Number.isFinite(premium) && Number.isFinite(positionSize)) {
        totals.value += premium * positionSize;
      }

      const stockPrice = equityPrice;
      const strike = Number(item.strike);
      if (
        Number.isFinite(stockPrice) &&
        stockPrice > 0 &&
        Number.isFinite(strike) &&
        strike > 0 &&
        item.expiry &&
        isValidDate(item.expiry) &&
        item.type
      ) {
        const days = daysBetween(tradingDate, item.expiry);
        const T = days / 365;
        if (T > 0) {
          const optType = item.type.toLowerCase();
          const iv = calculateImpliedVolatility(item.premium, stockPrice, item.strike, T, interest, dividendRatio, optType);

          if (typeof iv === 'number') {
            const delta = calculateBlackScholesDelta(stockPrice, item.strike, T, interest, iv, dividendRatio, optType);
            const gamma = calculateBlackScholesGamma(stockPrice, item.strike, T, interest, iv, dividendRatio);
            const theta = calculateBlackScholesTheta(stockPrice, item.strike, T, interest, iv, dividendRatio, optType);
            const vega = calculateBlackScholesVega(stockPrice, item.strike, T, interest, iv, dividendRatio) * 0.01;

            totals.delta += delta * positionSize;
            totals.gamma += gamma * positionSize;
            totals.theta += theta * positionSize;
            totals.vega += vega * positionSize;
          }
        }
      }
    }

    return totals;
  }, { value: 0, delta: 0, gamma: 0, theta: 0, vega: 0 });

  const formatCurrency = (value) =>
    `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const formatGreek = (value) =>
    value.toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 });



  return (
    <div ref={setNodeRef} className={`option-builder-container${isOver ? ' drop-target-active' : ''}`}>
      <div className="top-bar">
        <input
          type="text"
          value={title}
          onChange={(e) => onUpdateTitle(e.target.value)}
          className="portfolio-title"
        />
        <div className="action-buttons">
          <button
            onClick={() => setIsCollapsed((prev) => !prev)}
            className="collapse-toggle-btn"
            aria-label={isCollapsed ? 'Expand option builder' : 'Collapse option builder'}
            title={isCollapsed ? 'Expand' : 'Collapse'}
          >
            {isCollapsed ? '+' : '-'}
          </button>
          <button onClick={onAddStock} className="add-stock-btn" disabled={hasStock}>
            Add Stock
          </button>
          <button onClick={onAddOption} className="add-option-btn">
            + Add Option
          </button>
          <button onClick={onCopyBuilder} className="copy-combo-btn">
            Copy Combo
          </button>
          <button onClick={() => setIsDeleteComboDialogOpen(true)} className="delete-combo-btn">
            Delete Option Combo
          </button>
        </div>
      </div>
      <Dialog
        isOpen={isDeleteComboDialogOpen}
        title="Delete Option Combo"
        message={`Are you sure you want to delete the option combo "${title}"?`}
        onConfirm={() => { onRemoveBuilder(); setIsDeleteComboDialogOpen(false); }}
        onCancel={() => setIsDeleteComboDialogOpen(false)}
      />
      <Dialog
        isOpen={isDeleteItemDialogOpen}
        title="Delete Item"
        message={deleteMessage}
        onConfirm={() => {
          if (deletingItemId) {
            onRemoveOption(deletingItemId);
          }
          setIsDeleteItemDialogOpen(false);
          setDeletingItemId(null);
        }}
        onCancel={() => {
          setIsDeleteItemDialogOpen(false);
          setDeletingItemId(null);
        }}
      />
      {!isCollapsed && (
        <div className="options-table-container">
        <table className="options-table">
          <thead>
            <tr>
              <th>Option Type</th>
              <th>Expiry Date</th>
              <th>Strike</th>
              <th>Price</th>
              <th>Contracts</th>
              <th>Implied Volatility</th>
              <th>Greeks</th>
              <th>Total Value</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {options.map((item) => {
              if (item.cardType === 'stock') {
                return (
                  <StockCard
                    key={item.id}
                    itemId={item.id}
                    builderId={builderId}
                    price={item.price}
                    shares={item.shares}
                    onUpdate={(field, value) => handleUpdate(item.id, field, value)}
                    onRemove={() => {
                      setIsDeleteItemDialogOpen(true);
                      setDeletingItemId(item.id);
                    }}
                    isEditing={isEditing(item.id)}
                    onStartEdit={() => startEditing(item.id)}
                    onStopEdit={stopEditing}
                    onCancelEdit={cancelEditing}
                  />
                );
              }

              if (item.cardType === 'option') {
                return (
                  <OptionPositionRow
                    key={item.id}
                    item={item}
                    builderId={builderId}
                    isEditing={isEditing(item.id)}
                    onUpdate={handleUpdate}
                    onStartEdit={startEditing}
                    onStopEdit={stopEditing}
                    onCancelEdit={cancelEditing}
                    onDelete={() => {
                      setIsDeleteItemDialogOpen(true);
                      setDeletingItemId(item.id);
                    }}
                    ivInputValue={ivInputValue}
                    setIvInputValue={setIvInputValue}
                    stockPrice={equityPrice}
                    interest={interest}
                    dividendRatio={dividendRatio}
                    tradingDate={tradingDate}
                  />
                );
              }
              return null;
            })}
          </tbody>
        </table>
        </div>
      )}
      <div className="builder-totals-bar" aria-label="Option combo totals">
        <div className="builder-total-item"><strong>Total Value:</strong> {formatCurrency(comboTotals.value)}</div>
        <div className="builder-total-item"><strong>Total Delta:</strong> {formatGreek(comboTotals.delta)}</div>
        <div className="builder-total-item"><strong>Total Gamma:</strong> {formatGreek(comboTotals.gamma)}</div>
        <div className="builder-total-item"><strong>Total Theta:</strong> {formatGreek(comboTotals.theta)}</div>
        <div className="builder-total-item"><strong>Total V:</strong> {formatGreek(comboTotals.vega)}</div>
      </div>
    </div>
  );
};

export default OptionBuilder;
